import type { RouterOptions } from '@ts-rest/core'
import { initContract } from '@ts-rest/core'
import z from 'zod'
import { createSortQuerySchema, ErrorResponseSchema, PaginatedResultsQuerySchema } from '../../../shared/types'
import { PaginatedListOf } from '../schemas'
import * as Schemas from './cats.schemas'

export const CAT_SORT_COLUMNS = ['id', 'name', 'deactivatedAt', 'createdAt'] as const

const c = initContract()

const options: RouterOptions = {
  pathPrefix: '/animals/cats',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

export const animalCatsContract = c.router({
  createCat: {
    method: 'POST',
    path: '',
    body: Schemas.CreateCatBodySchema,
    responses: {
      201: Schemas.CatDtoSchema,
    },
  },
  previewCat: {
    method: 'POST',
    path: '/preview',
    body: Schemas.CreateCatBodySchema,
    responses: {
      200: Schemas.CatPreviewDtoSchema,
    },
  },
  updateCat: {
    method: 'PATCH',
    path: '/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: Schemas.UpdateCatBodySchema,
    responses: {
      200: Schemas.CatDtoSchema,
    },
  },
  getCats: {
    method: 'GET',
    path: '',
    query: PaginatedResultsQuerySchema.extend({
      userId: z.string().uuid(),
      sort: createSortQuerySchema(CAT_SORT_COLUMNS),
    }),
    responses: {
      200: PaginatedListOf(Schemas.CatDtoSchema),
    },
  },
  getCatBreeds: {
    method: 'GET',
    path: '/breeds',
    responses: {
      200: Schemas.CatBreedDtoSchema.array(),
    },
  },
  getCatHealthIssues: {
    method: 'GET',
    path: '/health-issues',
    responses: {
      200: Schemas.CatHealthIssueDtoSchema.array(),
    },
  },
  getCatFoodAllergens: {
    method: 'GET',
    path: '/food-allergens',
    responses: {
      200: Schemas.CatFoodAllergenDtoSchema.array(),
    },
  },
  getCatFoodTypes: {
    method: 'GET',
    path: '/food-types',
    description: 'Get all cat food types',
    responses: {
      200: Schemas.CatFoodTypeDtoSchema.array(),
    },
  },
  getCatDeactivationReasons: {
    method: 'GET',
    path: '/deactivation-reasons',
    responses: {
      200: Schemas.CatDeactivationReasonDtoSchema.array(),
    },
  },
  getCatById: {
    method: 'GET',
    path: '/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: Schemas.CatDtoSchema,
    },
  },
  deactivateCat: {
    method: 'POST',
    path: '/:id/deactivate',
    description: 'Deactivate a cat by its ID',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: Schemas.DeactivateCatBodySchema,
    responses: {
      200: Schemas.CatDtoSchema,
    },
  },
  reactivateCat: {
    method: 'POST',
    path: '/:id/reactivate',
    description: 'Reactivate a cat by its ID',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: null,
    responses: {
      200: Schemas.CatDtoSchema,
    },
  },
}, options)
